/**
 * Three dashboard modes — Action, Informative, Finance.
 * Backed by GET /api/dashboard/insights.
 * Designed to be visually rich (gradients, sparklines, color, motion) while keeping
 * tables/lists when they are the right tool.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ComposedChart,
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Clock, AlertTriangle,
  Award, FileText, ArrowRight, Sparkles, Target, Activity, ScrollText, Calendar,
} from 'lucide-react';

export const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const fmt0 = (v) => `$${(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtShort = (v) => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}k` : `$${v||0}`;

/* ── shared gradient KPI tile ────────────────────────────────────────── */
export function HeroTile({ icon: Icon, label, value, sub, gradient, sparkData, trend, onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ${gradient} ${interactive ? 'cursor-pointer transition-all hover:shadow-xl hover:-translate-y-0.5' : ''}`}
      data-testid={interactive ? `hero-tile-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined}
    >
      <div className="absolute -right-4 -top-4 opacity-15">
        <Icon className="h-24 w-24" strokeWidth={1.5} />
      </div>
      {interactive && (
        <div className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowRight className="h-4 w-4 text-white/70" />
        </div>
      )}
      <div className="relative">
        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider opacity-80">{label}</div>
        <div className="mt-1 text-3xl font-bold tabular-nums">{value}</div>
        {sub && <div className="mt-1 text-xs opacity-80">{sub}</div>}
        {trend != null && (
          <div className={`mt-2 inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-medium`}>
            {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(trend).toFixed(1)}% vs last period
          </div>
        )}
      </div>
      {sparkData && sparkData.length > 1 && (
        <div className="mt-2 h-10 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.7)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="spend" stroke="rgba(255,255,255,0.95)" strokeWidth={2} fill="url(#sparkFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function PanelCard({ title, subtitle, children, height = 'h-72', actions, accent, onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
      className={`rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden ${interactive ? 'cursor-pointer transition-shadow hover:shadow-md hover:border-indigo-300' : ''}`}
    >
      <div className={`flex items-center justify-between border-b border-zinc-100 px-5 py-3 ${accent || 'bg-zinc-50/60'}`}>
        <div>
          <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
            {title}
            {interactive && <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />}
          </div>
          {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        {actions}
      </div>
      <div className={`p-5 ${height}`}>{children}</div>
    </div>
  );
}

/* ── 1. ACTION dashboard ─────────────────────────────────────────────── */
export function ActionInsights({ insights, todos }) {
  const navigate = useNavigate();
  const go = (route) => () => navigate(route);
  const ms = insights?.monthly_spend || [];
  const cycle = insights?.cycle_time || [];
  const cycleSamples = cycle.filter(c => c.value != null);
  const cycleAvg = cycleSamples.length ? cycleSamples.reduce((a, c) => a + c.value, 0) / cycleSamples.length : 0;
  const ms_last = ms.slice(-6);
  const orders_trend = ms_last.length >= 2 ? ((ms_last[ms_last.length-1].orders - ms_last[0].orders) / Math.max(1, ms_last[0].orders) * 100) : 0;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <HeroTile icon={Sparkles} label="Action items" value={(todos || []).length}
          sub={`${(todos||[]).filter(t=>t.priority==='HIGH').length} high priority`}
          gradient="bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-700"
          onClick={go('/approvals')} />
        <HeroTile icon={ShoppingCart} label="POs YTD" value={insights?.headline?.orders_12m || 0}
          sub={`Avg ${fmtShort(insights?.headline?.avg_order_value || 0)}`}
          gradient="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700"
          sparkData={ms_last}
          trend={orders_trend}
          onClick={go('/orders')} />
        <HeroTile icon={Clock} label="Approval cycle" value={`${cycleAvg.toFixed(1)}h`}
          sub="Avg time to approve" gradient="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600"
          onClick={go('/approvals')} />
        <HeroTile icon={Target} label="Open RFx" value={insights?.headline?.rfx_open || 0}
          sub={`${insights?.headline?.contracts_expiring_90 || 0} contracts expiring 90d`}
          gradient="bg-gradient-to-br from-rose-500 via-pink-600 to-fuchsia-700"
          onClick={go('/rfq')} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PanelCard title="Top 5 items needing attention" subtitle="Sorted by priority" height="h-72" accent="bg-amber-50" onClick={go('/approvals')}>
          {(todos || []).length === 0 ? <div className="text-sm text-muted-foreground py-4">All caught up — nice.</div> : (
            <ul className="space-y-2 overflow-y-auto h-full">
              {(todos || []).slice(0, 8).map((t, i) => (
                <li key={i} className="flex items-start gap-3 rounded-lg border border-zinc-100 bg-white p-3 hover:border-indigo-300 transition-colors">
                  <div className={`mt-0.5 h-2 w-2 rounded-full ${t.priority === 'HIGH' ? 'bg-rose-500 animate-pulse' : t.priority === 'MEDIUM' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate">{t.title}</div>
                    {t.subtitle && <div className="text-xs text-muted-foreground truncate">{t.subtitle}</div>}
                  </div>
                  {t.amount != null && <span className="font-mono tabular-nums text-xs text-zinc-700">{fmtShort(t.amount)}</span>}
                </li>
              ))}
            </ul>
          )}
        </PanelCard>

        <PanelCard title="Approval cycle trend" subtitle="Average hours over time" height="h-72" accent="bg-indigo-50" onClick={go('/approvals')}>
          {cycle.filter(c => c.value != null).length === 0 ? <div className="text-xs text-muted-foreground">No approval data in window.</div> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cycle} margin={{ top: 8, right: 12, left: -10, bottom: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: '#71717a' }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={42}
                  tickFormatter={(v) => (v || '').split(' ')[0]}
                />
                <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}h`} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  labelFormatter={(label) => label}
                  formatter={(v, name, p) => v == null ? ['no data', 'Avg cycle'] : [`${v}h · n=${p.payload.count}`, 'Avg cycle']}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#6366f1"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#6366f1', stroke: '#fff', strokeWidth: 1.5 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </PanelCard>

        <PanelCard title="RFx pipeline" subtitle="Live activity snapshot" height="h-72" accent="bg-rose-50" onClick={go('/rfq')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={insights?.rfx_pipeline || []} layout="vertical" margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} width={110} />
              <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {(insights?.rfx_pipeline || []).map((_, i) => <Cell key={i} fill={PALETTE[(i + 4) % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </PanelCard>
      </div>
    </div>
  );
}

/* ── 2. INFORMATIVE dashboard (executive scorecard) ──────────────────── */
export function InformativeInsights({ insights }) {
  const navigate = useNavigate();
  const go = (route) => () => navigate(route);
  const ms = insights?.monthly_spend || [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <HeroTile icon={DollarSign} label="Spend (12m)" value={fmtShort(insights?.headline?.spend_12m || 0)}
          sub={`Across ${insights?.headline?.orders_12m || 0} POs`}
          gradient="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700"
          sparkData={ms.slice(-6)}
          onClick={go('/orders')} />
        <HeroTile icon={Award} label="Top supplier" value={insights?.top_suppliers?.[0]?.name?.split(' ')[0] || '—'}
          sub={insights?.top_suppliers?.[0] ? fmt0(insights.top_suppliers[0].value) : 'No data'}
          gradient="bg-gradient-to-br from-indigo-500 via-violet-600 to-purple-700"
          onClick={go('/data/suppliers')} />
        <HeroTile icon={ScrollText} label="Contracts at risk" value={insights?.headline?.contracts_expiring_90 || 0}
          sub="Expiring in 90 days" gradient="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600"
          onClick={go('/data/contracts')} />
        <HeroTile icon={Activity} label="Open RFx" value={insights?.headline?.rfx_open || 0}
          sub="Sourcing events in flight" gradient="bg-gradient-to-br from-rose-500 via-pink-600 to-fuchsia-700"
          onClick={go('/rfq')} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PanelCard title="Spend & volume — 12 months" subtitle="Where the money flows" height="h-80" accent="bg-emerald-50" onClick={go('/orders')}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={ms} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="spendArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, name) => name === 'spend' ? [fmt0(v), 'Spend'] : [v, 'Orders']} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="left" type="monotone" dataKey="spend" stroke="#10b981" fill="url(#spendArea)" strokeWidth={2.5} />
              <Bar yAxisId="right" dataKey="orders" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={16} />
            </ComposedChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Top suppliers by spend" subtitle="12-month rolling" height="h-80" accent="bg-indigo-50" onClick={go('/data/suppliers')}>
          {(insights?.top_suppliers || []).length === 0 ? <div className="text-xs text-muted-foreground">No supplier spend yet.</div> : (
            <div className="overflow-y-auto h-full">
              <table className="w-full text-sm">
                <tbody>
                  {(insights?.top_suppliers || []).slice(0, 8).map((s, i) => {
                    const max = Math.max(...insights.top_suppliers.map(r => r.value));
                    const pct = max ? Math.round((s.value / max) * 100) : 0;
                    return (
                      <tr
                        key={s.name}
                        className="border-b border-zinc-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors"
                        onClick={(e) => { e.stopPropagation(); navigate(`/data/suppliers?q=${encodeURIComponent(s.name)}`); }}
                      >
                        <td className="py-2 pr-3 w-6 text-xs text-zinc-400 tabular-nums text-right">{i+1}.</td>
                        <td className="py-2 pr-3"><div className="font-medium text-zinc-900 truncate">{s.name}</div>
                          <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                          </div>
                        </td>
                        <td className="py-2 pl-3 text-right font-mono tabular-nums text-zinc-900">{fmt0(s.value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </PanelCard>

        <PanelCard title="Contracts expiring — next 90 days" subtitle="Renewals on the horizon" height="h-80" accent="bg-rose-50" onClick={go('/data/contracts')}>
          {(insights?.expiring_contracts || []).length === 0 ? (
            <div className="text-xs text-muted-foreground py-4">No contracts expiring in the next 90 days.</div>
          ) : (
            <ul className="space-y-2 overflow-y-auto h-full">
              {(insights?.expiring_contracts || []).slice(0, 8).map((c, i) => (
                <li
                  key={i}
                  onClick={(e) => { e.stopPropagation(); navigate(`/data/contracts?tab=list&open=${encodeURIComponent(c.contract_number || '')}`); }}
                  className="flex items-start gap-3 rounded-lg border border-zinc-100 bg-white p-3 hover:border-rose-300 cursor-pointer transition-colors"
                >
                  <Calendar className="mt-0.5 h-4 w-4 text-rose-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate">{c.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.supplier_name}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-medium ${c.days_left < 14 ? 'text-rose-600' : c.days_left < 30 ? 'text-amber-600' : 'text-emerald-600'}`}>{c.days_left}d</div>
                    {c.value > 0 && <div className="text-[10px] text-zinc-500 font-mono">{fmtShort(c.value)}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </PanelCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PanelCard title="Spend by category" subtitle="Where the budget goes" height="h-72" accent="bg-violet-50" onClick={go('/orders')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={insights?.spend_by_category || []} layout="vertical" margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} width={100} />
              <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmt0(v)} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {(insights?.spend_by_category || []).map((_, i) => <Cell key={i} fill={PALETTE[(i+1) % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Supplier risk distribution" subtitle="Status mix across your supplier base" height="h-72" accent="bg-amber-50" onClick={go('/data/suppliers')}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={insights?.supplier_risk || []} dataKey="value" nameKey="name" cx="40%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                {(insights?.supplier_risk || []).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend verticalAlign="middle" align="right" layout="vertical" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </PanelCard>
      </div>
    </div>
  );
}

/* ── 3. FINANCE dashboard ────────────────────────────────────────────── */
export function FinanceInsights({ insights }) {
  const navigate = useNavigate();
  const go = (route) => () => navigate(route);
  const ms = insights?.monthly_spend || [];
  const aging = insights?.ap_aging || [];
  const overduePct = insights?.headline?.ap_total ? Math.round((insights.headline.ap_overdue / insights.headline.ap_total) * 100) : 0;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <HeroTile icon={DollarSign} label="AP outstanding" value={fmtShort(insights?.headline?.ap_total || 0)}
          sub={`${overduePct}% overdue`} gradient="bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-700"
          onClick={go('/invoices')} />
        <HeroTile icon={AlertTriangle} label="AP overdue" value={fmtShort(insights?.headline?.ap_overdue || 0)}
          sub="Action needed" gradient="bg-gradient-to-br from-rose-500 via-rose-600 to-red-700"
          onClick={go('/invoices')} />
        <HeroTile icon={DollarSign} label="Spend (12m)" value={fmtShort(insights?.headline?.spend_12m || 0)}
          sub={`${insights?.headline?.orders_12m || 0} POs`}
          gradient="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700"
          sparkData={ms.slice(-6)}
          onClick={go('/orders')} />
        <HeroTile icon={Target} label="Avg PO value" value={fmtShort(insights?.headline?.avg_order_value || 0)}
          sub="Per purchase order" gradient="bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-700"
          onClick={go('/orders')} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PanelCard title="AP aging" subtitle="Where the dollars are sitting" height="h-72" accent="bg-rose-50" onClick={go('/invoices')}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={aging} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmt0(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {aging.map((row, i) => {
                  const color = row.name === 'Current' ? '#10b981' : row.name === '1-30 days' ? '#84cc16' : row.name === '31-60 days' ? '#f59e0b' : row.name === '61-90 days' ? '#f97316' : '#ef4444';
                  return <Cell key={i} fill={color} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Invoice status mix" subtitle="Pipeline snapshot" height="h-72" accent="bg-indigo-50" onClick={go('/invoices')}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={insights?.inv_status || []} dataKey="value" nameKey="name" cx="40%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                {(insights?.inv_status || []).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(v, name, p) => [`${v} invoices · ${fmt0(p.payload.amount)}`, p.payload.name]} />
              <Legend verticalAlign="middle" align="right" layout="vertical" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </PanelCard>

        <PanelCard title="Top vendors by spend" subtitle="12-month rolling" height="h-72" accent="bg-violet-50" onClick={go('/data/suppliers')}>
          {(insights?.top_suppliers || []).length === 0 ? <div className="text-xs text-muted-foreground">No spend yet.</div> : (
            <div className="overflow-y-auto h-full">
              <table className="w-full text-sm">
                <tbody>
                  {(insights?.top_suppliers || []).slice(0, 8).map((s, i) => {
                    const max = Math.max(...insights.top_suppliers.map(r => r.value));
                    const pct = max ? Math.round((s.value / max) * 100) : 0;
                    return (
                      <tr
                        key={s.name}
                        className="border-b border-zinc-100 last:border-0 hover:bg-violet-50/40 cursor-pointer transition-colors"
                        onClick={(e) => { e.stopPropagation(); navigate(`/data/suppliers?q=${encodeURIComponent(s.name)}`); }}
                      >
                        <td className="py-2 pr-3 text-xs text-zinc-400 tabular-nums text-right w-6">{i+1}.</td>
                        <td className="py-2 pr-3"><div className="font-medium text-zinc-900 truncate">{s.name}</div>
                          <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                          </div>
                        </td>
                        <td className="py-2 pl-3 text-right font-mono tabular-nums">{fmt0(s.value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </PanelCard>
      </div>

      <PanelCard title="Monthly spend trend" subtitle="Track cash flow — 12 months" height="h-72" accent="bg-emerald-50" onClick={go('/orders')}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={ms} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmt0(v)} />
            <Area type="monotone" dataKey="spend" stroke="#10b981" fill="url(#trendFill)" strokeWidth={2.5} />
          </AreaChart>
        </ResponsiveContainer>
      </PanelCard>
    </div>
  );
}
